import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  customerOrderService,
  PublicMenuItem,
  PublicOrderItem,
  TableOrderInfo,
} from '../services/customerOrder';
import {
  UtensilsCrossed,
  Plus,
  Minus,
  ShoppingCart,
  Bell,
  X,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import VegIcon from '../assets/Veg.png';
import NonVegIcon from '../assets/Non_veg.png';

interface CartItem extends PublicOrderItem {
  itemName: string;
  specialNotes?: string;
}

export const TableOrders: React.FC = () => {
  const { restaurantId, tableId } = useParams<{ restaurantId: string; tableId: string }>();

  const [tableInfo, setTableInfo] = useState<TableOrderInfo | null>(null);
  const [menu, setMenu] = useState<PublicMenuItem[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [waiterCalling, setWaiterCalling] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');

  // Group menu by veg type
  const vegItems = menu.filter(i => i.vegType === 'VEG' || i.vegType === 'VEGAN');
  const nonVegItems = menu.filter(i => i.vegType === 'NON_VEG');

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    veg: true,
    nonveg: true,
  });

  useEffect(() => {
    if (!restaurantId || !tableId) return;

    const load = async () => {
      try {
        const [info, items] = await Promise.all([
          customerOrderService.getTableInfo(restaurantId, tableId),
          customerOrderService.getMenu(restaurantId),
        ]);
        setTableInfo(info);
        setMenu(items);
      } catch (err: any) {
        setError('Could not load table information. Please scan the QR code again.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [restaurantId, tableId]);

  const getQty = (itemId: string) => {
    return cart.find(c => c.itemId === itemId)?.quantity || 0;
  };

  const addToCart = (item: PublicMenuItem) => {
    setCart(prev => {
      const existing = prev.find(c => c.itemId === item.id);
      if (existing) {
        return prev.map(c => c.itemId === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, { itemId: item.id, itemName: item.name, quantity: 1, unitPrice: item.price }];
    });
  };

  const removeFromCart = (itemId: string) => {
    setCart(prev => {
      const existing = prev.find(c => c.itemId === itemId);
      if (!existing) return prev;
      if (existing.quantity <= 1) return prev.filter(c => c.itemId !== itemId);
      return prev.map(c => c.itemId === itemId ? { ...c, quantity: c.quantity - 1 } : c);
    });
  };

  const updateItemNotes = (itemId: string, notes: string) => {
    setCart(prev => prev.map(c => c.itemId === itemId ? { ...c, specialNotes: notes } : c));
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.unitPrice * c.quantity, 0);
  const cartCount = cart.reduce((sum, c) => sum + c.quantity, 0);

  const handlePlaceOrder = async () => {
    if (!customerName.trim()) { toast.error('Please enter your name'); return; }
    if (!/^[6-9]\d{9}$/.test(customerMobile)) { toast.error('Enter a valid 10-digit mobile number'); return; }
    if (cart.length === 0) { toast.error('Your cart is empty'); return; }

    setSubmitting(true);
    try {
      await customerOrderService.placeOrderRequest(restaurantId!, tableId!, {
        restaurantId: restaurantId!,
        tableId: tableId!,
        customerName: customerName.trim(),
        customerMobile: customerMobile.trim(),
        items: cart.map(c => ({
          itemId: c.itemId,
          itemName: c.itemName,
          quantity: c.quantity,
          unitPrice: c.unitPrice,
          specialNotes: c.specialNotes,
        })),
      });
      setOrderSuccess(true);
      setCart([]);
      setShowOrderForm(false);
      setShowCart(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to place order request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCallWaiter = async () => {
    setWaiterCalling(true);
    try {
      await customerOrderService.callWaiter(restaurantId!, tableId!);
      toast.success('Waiter has been called! They will be with you shortly.');
    } catch {
      toast.error('Could not call waiter. Please try again.');
    } finally {
      setWaiterCalling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-orange-500 mx-auto mb-3" />
          <p className="text-gray-600">Loading menu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <UtensilsCrossed className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Oops!</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      <Toaster position="top-center" />

      {/* Header */}
      <div className="bg-orange-500 text-white px-4 py-5 sticky top-0 z-30 shadow-md">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <UtensilsCrossed className="h-5 w-5" />
                {tableInfo?.table?.name || 'Your Table'}
              </h1>
              <p className="text-orange-100 text-sm mt-0.5">Scan &amp; Order · Self Service</p>
            </div>
            <button
              onClick={handleCallWaiter}
              disabled={waiterCalling}
              className="flex items-center gap-2 bg-white text-orange-600 px-3 py-2 rounded-xl font-semibold text-sm disabled:opacity-60 shadow"
            >
              <Bell className="h-4 w-4" />
              {waiterCalling ? 'Calling...' : 'Call Waiter'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-4 space-y-4">

        {/* Current orders */}
        {tableInfo?.hasOpenOrder && tableInfo.currentOrders?.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-semibold text-blue-800 text-sm">
              Current Order{tableInfo.currentOrders.length > 1 ? `s (${tableInfo.currentOrders.length})` : ''}
            </h3>
            {tableInfo.currentOrders.map((order: any, orderIdx: number) => (
              <div key={order.id || orderIdx} className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                {tableInfo.currentOrders.length > 1 && (
                  <p className="text-xs font-semibold text-blue-500 mb-2 uppercase tracking-wide">
                    Order {orderIdx + 1}
                  </p>
                )}
                <div className="space-y-1">
                  {order.items?.map((item: any, idx: number) => (
                    <div key={idx} className="flex justify-between text-sm text-blue-700">
                      <span>{item.name} × {item.quantity}</span>
                      <span>₹{(item.unitPrice * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between font-semibold text-blue-800">
                    <span>Total</span>
                    <span>₹{order.total?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Order success */}
        {orderSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
            <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-2" />
            <h3 className="font-bold text-green-800 text-lg">Order Request Sent!</h3>
            <p className="text-green-700 text-sm mt-1">
              A waiter will confirm your order shortly. Please wait.
            </p>
            <button
              onClick={() => setOrderSuccess(false)}
              className="mt-3 text-green-700 underline text-sm"
            >
              Order more
            </button>
          </div>
        )}

        {/* Menu sections */}
        {vegItems.length > 0 && (
          <MenuSection
            title="Veg / Vegan"
            icon="🥗"
            items={vegItems}
            expanded={expandedGroups.veg}
            onToggle={() => setExpandedGroups(prev => ({ ...prev, veg: !prev.veg }))}
            getQty={getQty}
            onAdd={addToCart}
            onRemove={removeFromCart}
          />
        )}

        {nonVegItems.length > 0 && (
          <MenuSection
            title="Non-Veg"
            icon="🍗"
            items={nonVegItems}
            expanded={expandedGroups.nonveg}
            onToggle={() => setExpandedGroups(prev => ({ ...prev, nonveg: !prev.nonveg }))}
            getQty={getQty}
            onAdd={addToCart}
            onRemove={removeFromCart}
          />
        )}

        {menu.length === 0 && (
          <div className="text-center py-16">
            <UtensilsCrossed className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No menu items available at the moment.</p>
          </div>
        )}
      </div>

      {/* Floating cart button */}
      {cartCount > 0 && !showCart && !showOrderForm && (
        <div className="fixed bottom-6 left-0 right-0 z-40 flex justify-center px-4">
          <button
            onClick={() => setShowCart(true)}
            className="flex items-center gap-3 bg-orange-500 text-white px-6 py-3 rounded-2xl shadow-xl font-semibold w-full max-w-sm justify-between"
          >
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              <span>{cartCount} item{cartCount !== 1 ? 's' : ''}</span>
            </div>
            <span>₹{cartTotal.toFixed(2)} →</span>
          </button>
        </div>
      )}

      {/* Cart Panel */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end">
          <div className="bg-white w-full rounded-t-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-orange-500" /> Your Order
              </h2>
              <button onClick={() => setShowCart(false)} className="text-gray-500">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
              {cart.map(item => (
                <div key={item.itemId} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{item.itemName}</p>
                      <p className="text-xs text-gray-500">₹{item.unitPrice.toFixed(2)} each</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => removeFromCart(item.itemId)}
                        className="w-7 h-7 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="font-semibold text-gray-900 w-5 text-center">{item.quantity}</span>
                      <button onClick={() => addToCart({ id: item.itemId, name: item.itemName, price: item.unitPrice, vegType: 'VEG', available: true })}
                        className="w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center">
                        <Plus className="h-3 w-3" />
                      </button>
                      <span className="ml-2 font-semibold text-gray-700 w-16 text-right text-sm">
                        ₹{(item.unitPrice * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={item.specialNotes || ''}
                    onChange={e => updateItemNotes(item.itemId, e.target.value)}
                    placeholder="Add a note for this item (optional)"
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-300"
                  />
                </div>
              ))}
            </div>

            <div className="px-5 py-4 border-t bg-gray-50">
              <div className="flex justify-between mb-4 font-bold text-gray-900">
                <span>Total</span>
                <span>₹{cartTotal.toFixed(2)}</span>
              </div>
              <button
                onClick={() => { setShowCart(false); setShowOrderForm(true); }}
                className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold"
              >
                Proceed to Place Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer details form */}
      {showOrderForm && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end">
          <div className="bg-white w-full rounded-t-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">Your Details</h2>
              <button onClick={() => setShowOrderForm(false)} className="text-gray-500">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                Please provide your details to place the order.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  placeholder="Your name"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                <input
                  type="tel"
                  value={customerMobile}
                  onChange={e => setCustomerMobile(e.target.value)}
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>

              <div className="bg-orange-50 rounded-xl p-3">
                <p className="text-sm font-medium text-orange-800 mb-2">{cartCount} item{cartCount !== 1 ? 's' : ''} · ₹{cartTotal.toFixed(2)}</p>
                {cart.map(item => (
                  <div key={item.itemId} className="flex justify-between text-sm text-orange-700">
                    <span>{item.itemName} × {item.quantity}</span>
                    <span>₹{(item.unitPrice * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={handlePlaceOrder}
                disabled={submitting}
                className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Placing Order...</> : 'Place Order Request'}
              </button>

              <p className="text-xs text-gray-500 text-center">
                A waiter will confirm your order before it is processed.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Menu Section component ─────────────────────────────────────────────────────
interface MenuSectionProps {
  title: string;
  icon: string;
  items: PublicMenuItem[];
  expanded: boolean;
  onToggle: () => void;
  getQty: (id: string) => number;
  onAdd: (item: PublicMenuItem) => void;
  onRemove: (id: string) => void;
}

const MenuSection: React.FC<MenuSectionProps> = ({
  title, icon, items, expanded, onToggle, getQty, onAdd, onRemove,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b"
      >
        <span className="font-semibold text-gray-800">{icon} {title} ({items.length})</span>
        {expanded ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
      </button>

      {expanded && (
        <div className="divide-y divide-gray-100">
          {items.map(item => {
            const qty = getQty(item.id);
            return (
              <div key={item.id} className="flex items-center justify-between px-4 py-3 gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <img
                    src={item.vegType === 'NON_VEG' ? NonVegIcon : VegIcon}
                    alt={item.vegType}
                    className="w-4 h-4 flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{item.name}</p>
                    <p className="text-orange-600 font-semibold text-sm">₹{item.price.toFixed(2)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  {qty === 0 ? (
                    <button
                      onClick={() => onAdd(item)}
                      className="bg-orange-500 text-white px-4 py-1.5 rounded-lg text-sm font-semibold"
                    >
                      Add
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 border border-orange-400 rounded-lg px-2 py-1">
                      <button onClick={() => onRemove(item.id)} className="text-orange-500">
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="font-bold text-gray-900 text-sm w-4 text-center">{qty}</span>
                      <button onClick={() => onAdd(item)} className="text-orange-500">
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
